import { cn } from '@/utils/cn'

export interface LoaderProps {
  size?: 'sm' | 'md' | 'lg'
  variant?: 'spinner' | 'dots' | 'pulse'
  className?: string
  text?: string
}

const Loader = ({
  size = 'md',
  variant = 'spinner',
  className,
  text,
}: LoaderProps) => {
  const sizes = {
    sm: 'w-4 h-4',
    md: 'w-8 h-8',
    lg: 'w-12 h-12',
  }

  const textSizes = {
    sm: 'text-xs',
    md: 'text-sm',
    lg: 'text-base',
  }

  if (variant === 'spinner') {
    return (
      <div className={cn('flex flex-col items-center gap-3', className)}>
        <div
          className={cn(
            'animate-spin rounded-full border-2 border-slate-200 dark:border-slate-700',
            'border-t-light-icy-blue dark:border-t-dark-muted-teal',
            sizes[size]
          )}
        />
        {text && (
          <p className={cn('text-slate-500 dark:text-slate-400', textSizes[size])}>
            {text}
          </p>
        )}
      </div>
    )
  }

  if (variant === 'dots') {
    return (
      <div className={cn('flex items-center gap-1.5', className)}>
        {[0, 1, 2].map((i) => (
          <div
            key={i}
            className={cn(
              'rounded-full bg-light-icy-blue dark:bg-dark-muted-teal',
              'animate-pulse',
              size === 'sm' && 'w-1.5 h-1.5',
              size === 'md' && 'w-2 h-2',
              size === 'lg' && 'w-3 h-3'
            )}
            style={{
              animationDelay: `${i * 0.15}s`,
            }}
          />
        ))}
        {text && (
          <p className={cn('ml-2 text-slate-500 dark:text-slate-400', textSizes[size])}>
            {text}
          </p>
        )}
      </div>
    )
  }

  // Pulse variant
  return (
    <div className={cn('flex flex-col items-center gap-3', className)}>
      <div
        className={cn(
          'rounded-full bg-light-icy-blue/30 dark:bg-dark-muted-teal/30',
          'animate-pulse-soft',
          sizes[size]
        )}
      />
      {text && (
        <p className={cn('text-slate-500 dark:text-slate-400', textSizes[size])}>
          {text}
        </p>
      )}
    </div>
  )
}

// Full page loader
export const PageLoader = ({ text = 'Loading...' }: { text?: string }) => {
  return (
    <div className="fixed inset-0 flex items-center justify-center bg-white/80 dark:bg-slate-900/80 backdrop-blur-sm z-50">
      <Loader size="lg" text={text} />
    </div>
  )
}

// Inline skeleton loader
export interface SkeletonProps {
  className?: string
  width?: string | number
  height?: string | number
  rounded?: 'none' | 'sm' | 'md' | 'lg' | 'full'
}

export const Skeleton = ({
  className,
  width,
  height,
  rounded = 'md',
}: SkeletonProps) => {
  const roundedClasses = {
    none: 'rounded-none',
    sm: 'rounded',
    md: 'rounded-lg',
    lg: 'rounded-xl',
    full: 'rounded-full',
  }

  return (
    <div
      className={cn(
        'shimmer',
        roundedClasses[rounded],
        className
      )}
      style={{
        width: typeof width === 'number' ? `${width}px` : width,
        height: typeof height === 'number' ? `${height}px` : height,
      }}
    />
  )
}

export default Loader
